import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listAdmins, createAdmin, removeAdmin, changePassword, waRegisterBegin, waRegisterVerify, waListCredentials, waDeleteCredential, updateProfile } from "../lib/api";
import { registerBiometric, platformBiometricAvailable, webAuthnSupported } from "../lib/webauthn";
import { telHref } from "../lib/phone";
import { useAuth } from "../auth";
import { ArrowLeft, UserPlus, Trash2, KeyRound, ShieldCheck, Fingerprint, ScanFace, Smartphone, Phone } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";

export default function AdminUsers() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [profile, setProfile] = useState({ name: "", phone: "" });
  const [bioSupported, setBioSupported] = useState(false);
  const [bioAvailable, setBioAvailable] = useState(false);
  const [bioCreds, setBioCreds] = useState([]);
  const [bioBusy, setBioBusy] = useState(false);
  const [deviceLabel, setDeviceLabel] = useState("");

  const load = () => {
    setLoading(true);
    listAdmins().then(setAdmins).catch(() => setAdmins([])).finally(() => setLoading(false));
    waListCredentials().then(setBioCreds).catch(() => setBioCreds([]));
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    setBioSupported(webAuthnSupported());
    platformBiometricAvailable().then(setBioAvailable);
    const ua = navigator.userAgent;
    const label = /iPad|iPhone|iPod/i.test(ua) ? "iOS Cihazı (Face ID)"
      : /Android/i.test(ua) ? "Android Cihazı (Parmak İzi / Yüz)"
      : /Mac/i.test(ua) ? "Mac (Touch ID)"
      : /Windows/i.test(ua) ? "Windows Hello"
      : "Bu Cihaz";
    setDeviceLabel(label);
  }, []);

  useEffect(() => {
    if (user) setProfile({ name: user.name || "", phone: user.phone || "" });
  }, [user]);

  const enrollBiometric = async () => {
    setBioBusy(true);
    try {
      const options = await waRegisterBegin();
      const payload = await registerBiometric(options, deviceLabel);
      await waRegisterVerify(payload);
      toast.success("Biyometrik giriş bu cihazda etkinleştirildi");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      let msg;
      if (typeof detail === "string") msg = detail;
      else if (e?.name === "NotAllowedError") msg = "Kayıt iptal edildi veya zaman aşımına uğradı";
      else if (e?.name === "SecurityError") { msg = "Cihaz güvenlik kontrolü başarısız oldu"; console.error("WebAuthn SecurityError:", e.message); }
      else if (e?.name === "InvalidStateError") msg = "Bu cihaz zaten kayıtlı";
      else if (e?.name === "NotSupportedError") msg = "Bu cihaz biyometrik doğrulamayı desteklemiyor";
      else { msg = "Kayıt tamamlanamadı"; console.error("WebAuthn error:", e); }
      toast.error(msg);
    } finally { setBioBusy(false); }
  };

  const removeBio = async (cred_id) => {
    if (!window.confirm("Bu cihazın biyometrik kaydı silinsin mi?")) return;
    try { await waDeleteCredential(cred_id); toast.success("Silindi"); load(); }
    catch { toast.error("Silinemedi"); }
  };

  const add = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("Şifre en az 8 karakter olmalı"); return; }
    if (!form.phone || form.phone.replace(/\D/g, "").length < 10) { toast.error("Geçerli bir telefon numarası girin"); return; }
    try {
      await createAdmin(form);
      toast.success("Yönetici eklendi");
      setForm({ email: "", password: "", name: "", phone: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eklenemedi");
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      await updateProfile(profile);
      await refresh();
      toast.success("Bilgileriniz güncellendi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    }
  };

  const del = async (u) => {
    if (!window.confirm(`${u.email} kaldırılsın mı?`)) return;
    try { await removeAdmin(u.user_id); toast.success("Kaldırıldı"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi"); }
  };

  const submitPw = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 8) { toast.error("Yeni şifre en az 8 karakter"); return; }
    try {
      await changePassword(pw.current_password, pw.new_password);
      toast.success("Şifreniz güncellendi");
      setPw({ current_password: "", new_password: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    }
  };

  return (
    <div data-testid="admin-users-page" className="min-h-screen bg-slate-100 py-10">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft className="w-4 h-4" /> Panele dön</Link>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Admins list */}
          <div className="lg:col-span-3 bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-6">
              <ShieldCheck className="w-4 h-4 text-[#C0562F]" />
              <h1 className="font-display text-2xl text-slate-900">Yöneticiler ({admins.length})</h1>
            </div>
            {loading ? <div className="text-slate-500">Yükleniyor…</div> : (
              <ul className="divide-y divide-slate-100">
                {admins.map((a) => (
                  <li key={a.user_id} data-testid={`admin-user-${a.user_id}`} className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate">{a.name || "—"}</div>
                      <div className="text-xs text-slate-500 truncate" data-testid={`admin-user-email-${a.user_id}`}>{a.email}</div>
                      <div className="text-xs text-slate-500 mt-0.5" data-testid={`admin-user-phone-${a.user_id}`}>
                        {a.phone ? (
                          <a href={telHref(a.phone)} className="hover:text-[#C0562F]">{a.phone}</a>
                        ) : (
                          <span className="italic text-slate-400">Telefon yok</span>
                        )}
                      </div>
                    </div>
                    {a.user_id === user?.user_id ? (
                      <span className="text-[10px] tracking-widest uppercase text-emerald-700 bg-emerald-50 px-2 py-1 rounded shrink-0">Siz</span>
                    ) : (
                      <button onClick={() => del(a)} data-testid={`admin-remove-${a.user_id}`} className="w-8 h-8 rounded-md text-red-600 hover:bg-red-50 flex items-center justify-center shrink-0"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Add admin */}
          <div className="lg:col-span-2 space-y-6">
            <form onSubmit={add} data-testid="new-admin-form" className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-5">
                <UserPlus className="w-4 h-4 text-[#C0562F]" />
                <div className="font-display text-lg text-slate-900">Yeni Yönetici</div>
              </div>
              <div className="space-y-3">
                <div><label className="label-caps block mb-1">Ad</label><Input data-testid="new-admin-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><label className="label-caps block mb-1">E-posta</label><Input data-testid="new-admin-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
                <div><label className="label-caps block mb-1">Telefon</label><Input data-testid="new-admin-phone" type="tel" placeholder="0-5XX-XXXXXXX" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required /></div>
                <div><label className="label-caps block mb-1">Şifre (min 8)</label><Input data-testid="new-admin-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} /></div>
              </div>
              <p className="text-[11px] text-slate-500 mt-3">Bu numara yeni yöneticinin ilanlarında müşterilere gösterilir.</p>
              <button type="submit" data-testid="new-admin-submit" className="mt-4 w-full btn-accent h-11 rounded-full font-semibold text-sm">Yönetici Ekle</button>
            </form>

            <form onSubmit={saveProfile} data-testid="profile-form" className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-5">
                <Phone className="w-4 h-4 text-[#C0562F]" />
                <div className="font-display text-lg text-slate-900">Bilgilerim</div>
              </div>
              <div className="space-y-3">
                <div><label className="label-caps block mb-1">Ad Soyad</label><Input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} /></div>
                <div><label className="label-caps block mb-1">Telefon</label><Input data-testid="profile-phone" type="tel" placeholder="0-5XX-XXXXXXX" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} /></div>
              </div>
              <p className="text-[11px] text-slate-500 mt-3">Bu numara sizin oluşturduğunuz ilanlarda müşterilerin göreceği iletişim bilgisi olur.</p>
              <button type="submit" data-testid="profile-save" className="mt-4 w-full btn-primary h-11 rounded-full font-semibold text-sm">Kaydet</button>
            </form>

            <form onSubmit={submitPw} data-testid="change-password-form" className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-5">
                <KeyRound className="w-4 h-4 text-[#C0562F]" />
                <div className="font-display text-lg text-slate-900">Şifremi Değiştir</div>
              </div>
              <div className="space-y-3">
                <div><label className="label-caps block mb-1">Mevcut Şifre</label><Input data-testid="cp-current" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} required /></div>
                <div><label className="label-caps block mb-1">Yeni Şifre (min 8)</label><Input data-testid="cp-new" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} required minLength={8} /></div>
              </div>
              <button type="submit" data-testid="cp-submit" className="mt-5 w-full btn-primary h-11 rounded-full font-semibold text-sm">Şifreyi Güncelle</button>
            </form>

            <div data-testid="biometric-section" className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <ScanFace className="w-4 h-4 text-[#C0562F]" />
                <div className="font-display text-lg text-slate-900">Biyometrik Giriş</div>
              </div>
              {!bioSupported ? (
                <p className="text-sm text-slate-500">Bu tarayıcı WebAuthn'i desteklemiyor.</p>
              ) : !bioAvailable ? (
                <p className="text-sm text-slate-500">Bu cihazda platform biyometriği (Face ID / Touch ID / Parmak İzi) bulunamadı.</p>
              ) : (
                <>
                  <p className="text-sm text-slate-600 mb-4">iPhone'da Face ID, Android'de parmak izi/yüz tanıma, Mac'te Touch ID ile şifresiz giriş yapabilirsiniz. Bu kayıt sadece bu cihaz için geçerlidir.</p>
                  <button onClick={enrollBiometric} disabled={bioBusy} data-testid="enroll-biometric" className="w-full h-11 rounded-full bg-gradient-to-r from-[#C0562F] to-[#A04726] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
                    <Fingerprint className="w-4 h-4" /> {bioBusy ? "Kaydediliyor…" : "Bu Cihazı Biyometrik ile Kaydet"}
                  </button>
                </>
              )}

              {bioCreds.length > 0 && (
                <div className="mt-6">
                  <div className="label-caps mb-2">Kayıtlı Cihazlar ({bioCreds.length})</div>
                  <ul className="divide-y divide-slate-100">
                    {bioCreds.map((c) => (
                      <li key={c.credential_id} className="py-2 flex items-center justify-between gap-2" data-testid={`bio-cred-${c.credential_id.slice(0, 8)}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <Smartphone className="w-4 h-4 text-slate-500 shrink-0" />
                          <div className="truncate text-sm text-slate-800">{c.device_label}</div>
                        </div>
                        <button onClick={() => removeBio(c.credential_id)} className="w-7 h-7 rounded-md text-red-600 hover:bg-red-50 flex items-center justify-center shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
